import { createServerFn } from "@tanstack/react-start";
import { and, asc, eq, gte, lt, sql } from "drizzle-orm";
import { nanoid } from "nanoid";
import { z } from "zod";
import {
  project,
  projectExclusionCodes,
  projectInvitation,
  projectMember,
  user,
} from "~/lib/db/schema";

const updateProjectSchema = z.object({
  projectId: z.string(),
  title: z
    .string()
    .min(1, "Title is required")
    .max(255, "Title must be less than 255 characters"),
  description: z
    .string()
    .max(2000, "Description must be less than 2000 characters")
    .optional(),
});

export const $updateProject = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => updateProjectSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectOwner, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectOwner(data.projectId, currentUser.id);

    const [updated] = await db
      .update(project)
      .set({
        title: data.title,
        description: data.description,
      })
      .where(eq(project.id, data.projectId))
      .returning();

    return updated;
  });

const projectIdSchema = z.object({
  projectId: z.string(),
});

export const $getProjectMembers = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectAccess(data.projectId, currentUser.id);

    // Get the project owner
    const foundProject = await db.query.project.findFirst({
      where: eq(project.id, data.projectId),
      with: {
        user: true,
      },
    });

    if (!foundProject) {
      throw new Error("Project not found");
    }

    // Get all members
    const members = await db.query.projectMember.findMany({
      where: eq(projectMember.projectId, data.projectId),
      with: {
        user: true,
        addedByUser: true,
      },
      orderBy: (member, { asc }) => [asc(member.addedAt)],
    });

    return {
      owner: {
        id: foundProject.userId,
        name: foundProject.user.name,
        email: foundProject.user.email,
        image: foundProject.user.image,
      },
      members: members.map((m) => ({
        id: m.id,
        userId: m.userId,
        name: m.user.name,
        email: m.user.email,
        image: m.user.image,
        role: m.role,
        addedAt: m.addedAt,
        addedByName: m.addedByUser?.name ?? null,
      })),
    };
  });

export const $getProjectInvitations = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectAccess(data.projectId, currentUser.id);
    const now = new Date();

    // Keep invitation status consistent with expiration time.
    await db
      .update(projectInvitation)
      .set({ status: "expired" })
      .where(
        and(
          eq(projectInvitation.projectId, data.projectId),
          eq(projectInvitation.status, "pending"),
          lt(projectInvitation.expiresAt, now),
        ),
      );

    const invitations = await db.query.projectInvitation.findMany({
      where: and(
        eq(projectInvitation.projectId, data.projectId),
        eq(projectInvitation.status, "pending"),
        gte(projectInvitation.expiresAt, now),
      ),
      with: {
        invitedByUser: true,
      },
      orderBy: (inv, { desc }) => [desc(inv.createdAt)],
    });

    return invitations.map((inv) => ({
      id: inv.id,
      email: inv.email,
      role: inv.role,
      status: inv.status,
      createdAt: inv.createdAt,
      expiresAt: inv.expiresAt,
      invitedByName: inv.invitedByUser.name,
    }));
  });

const inviteReviewerSchema = z.object({
  projectId: z.string(),
  email: z.string().email("Invalid email address"),
});

export const $inviteReviewer = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => inviteReviewerSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectOwner, db } =
      await import("~/lib/server/helpers.server");
    const { env } = await import("~/env/server");
    const currentUser = await requireUser();
    const foundProject = await requireProjectOwner(data.projectId, currentUser.id);

    const email = data.email.toLowerCase().trim();
    const now = new Date();

    // Expire stale pending invites before dedupe checks.
    await db
      .update(projectInvitation)
      .set({ status: "expired" })
      .where(
        and(
          eq(projectInvitation.projectId, data.projectId),
          eq(projectInvitation.email, email),
          eq(projectInvitation.status, "pending"),
          lt(projectInvitation.expiresAt, now),
        ),
      );

    // Check if user is already the owner
    const owner = await db.query.user.findFirst({
      where: eq(user.email, email),
    });

    if (owner && owner.id === foundProject.userId) {
      throw new Error("Cannot invite the project owner");
    }

    // Check if user is already a member
    if (owner) {
      const existingMember = await db.query.projectMember.findFirst({
        where: and(
          eq(projectMember.projectId, data.projectId),
          eq(projectMember.userId, owner.id),
        ),
      });

      if (existingMember) {
        throw new Error("User is already a member of this project");
      }
    }

    // Check if there's a pending invitation
    const existingInvitation = await db.query.projectInvitation.findFirst({
      where: and(
        eq(projectInvitation.projectId, data.projectId),
        eq(projectInvitation.email, email),
        eq(projectInvitation.status, "pending"),
        gte(projectInvitation.expiresAt, now),
      ),
    });

    if (existingInvitation) {
      throw new Error("An invitation has already been sent to this email");
    }

    // Create the invitation
    const token = nanoid(32);
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    const [invitation] = await db
      .insert(projectInvitation)
      .values({
        id: nanoid(),
        projectId: data.projectId,
        email,
        role: "reviewer",
        token,
        invitedBy: currentUser.id,
        expiresAt,
      })
      .returning();

    // Send the email
    const { sendProjectInvitation } = await import("~/lib/email/service");
    const emailResult = await sendProjectInvitation({
      to: email,
      inviterName: currentUser.name,
      projectTitle: foundProject.title,
      token,
    });

    if (!emailResult.success) {
      console.warn("Failed to send invitation email:", emailResult.error);
    }

    const inviteLink = `${env.VITE_BASE_URL}/invitations/accept?token=${token}`;

    return {
      id: invitation.id,
      email: invitation.email,
      emailSent: emailResult.success,
      inviteLink,
    };
  });

const cancelInvitationSchema = z.object({
  projectId: z.string(),
  invitationId: z.string(),
});

export const $cancelInvitation = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => cancelInvitationSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectOwner, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectOwner(data.projectId, currentUser.id);

    const invitation = await db.query.projectInvitation.findFirst({
      where: and(
        eq(projectInvitation.id, data.invitationId),
        eq(projectInvitation.projectId, data.projectId),
        eq(projectInvitation.status, "pending"),
      ),
    });

    if (!invitation) {
      throw new Error("Invitation not found");
    }

    await db
      .update(projectInvitation)
      .set({ status: "cancelled" })
      .where(eq(projectInvitation.id, data.invitationId));

    return { success: true };
  });

const removeMemberSchema = z.object({
  projectId: z.string(),
  memberId: z.string(),
});

export const $removeMember = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => removeMemberSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectOwner, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectOwner(data.projectId, currentUser.id);

    const member = await db.query.projectMember.findFirst({
      where: and(
        eq(projectMember.id, data.memberId),
        eq(projectMember.projectId, data.projectId),
      ),
    });

    if (!member) {
      throw new Error("Member not found");
    }

    await db.delete(projectMember).where(eq(projectMember.id, data.memberId));

    return { success: true };
  });

// Re-export helper for use in other modules (server-side only)
export const requireProjectAccess = async (projectId: string, userId: string) => {
  const helpers = await import("~/lib/server/helpers.server");
  return helpers.requireProjectAccess(projectId, userId);
};

// ===============================
// Exclusion Codes CRUD
// ===============================

export const $getProjectExclusionCodes = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectAccess(data.projectId, currentUser.id);

    const codes = await db.query.projectExclusionCodes.findMany({
      where: eq(projectExclusionCodes.projectId, data.projectId),
      orderBy: [asc(projectExclusionCodes.sortOrder)],
    });

    return codes;
  });

const createExclusionCodeSchema = z.object({
  projectId: z.string(),
  code: z
    .string()
    .min(1)
    .max(50)
    .regex(/^[A-Z_]+$/, "Code must be uppercase letters and underscores only"),
  label: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
});

export const $createExclusionCode = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => createExclusionCodeSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectOwner, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectOwner(data.projectId, currentUser.id);

    // Get the highest sort order
    const maxOrderResult = await db
      .select({
        maxOrder: sql<number>`COALESCE(MAX(${projectExclusionCodes.sortOrder}), -1)`,
      })
      .from(projectExclusionCodes)
      .where(eq(projectExclusionCodes.projectId, data.projectId));

    const nextOrder = (maxOrderResult[0]?.maxOrder ?? -1) + 1;

    const [newCode] = await db
      .insert(projectExclusionCodes)
      .values({
        id: nanoid(),
        projectId: data.projectId,
        code: data.code,
        label: data.label,
        description: data.description,
        sortOrder: nextOrder,
        isActive: true,
      })
      .returning();

    return newCode;
  });

const updateExclusionCodeSchema = z.object({
  projectId: z.string(),
  codeId: z.string(),
  code: z
    .string()
    .min(1)
    .max(50)
    .regex(/^[A-Z_]+$/, "Code must be uppercase letters and underscores only")
    .optional(),
  label: z.string().min(1).max(100).optional(),
  description: z.string().max(500).optional().nullable(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const $updateExclusionCode = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => updateExclusionCodeSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectOwner, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectOwner(data.projectId, currentUser.id);

    // Verify code exists and belongs to project
    const existingCode = await db.query.projectExclusionCodes.findFirst({
      where: and(
        eq(projectExclusionCodes.id, data.codeId),
        eq(projectExclusionCodes.projectId, data.projectId),
      ),
    });

    if (!existingCode) {
      throw new Error("Exclusion code not found");
    }

    const updateData: Partial<typeof existingCode> = {};
    if (data.code !== undefined) updateData.code = data.code;
    if (data.label !== undefined) updateData.label = data.label;
    if (data.description !== undefined) updateData.description = data.description;
    if (data.isActive !== undefined) updateData.isActive = data.isActive;
    if (data.sortOrder !== undefined) updateData.sortOrder = data.sortOrder;

    const [updated] = await db
      .update(projectExclusionCodes)
      .set(updateData)
      .where(eq(projectExclusionCodes.id, data.codeId))
      .returning();

    return updated;
  });

const deleteExclusionCodeSchema = z.object({
  projectId: z.string(),
  codeId: z.string(),
});

export const $deleteExclusionCode = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => deleteExclusionCodeSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectOwner, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectOwner(data.projectId, currentUser.id);

    // Verify code exists and belongs to project
    const existingCode = await db.query.projectExclusionCodes.findFirst({
      where: and(
        eq(projectExclusionCodes.id, data.codeId),
        eq(projectExclusionCodes.projectId, data.projectId),
      ),
    });

    if (!existingCode) {
      throw new Error("Exclusion code not found");
    }

    await db
      .delete(projectExclusionCodes)
      .where(eq(projectExclusionCodes.id, data.codeId));

    return { success: true };
  });

const reorderExclusionCodesSchema = z.object({
  projectId: z.string(),
  codeIds: z.array(z.string()),
});

export const $reorderExclusionCodes = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => reorderExclusionCodesSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectOwner, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectOwner(data.projectId, currentUser.id);

    // Update each code's sort order based on position in array
    await Promise.all(
      data.codeIds.map((codeId, index) =>
        db
          .update(projectExclusionCodes)
          .set({ sortOrder: index })
          .where(
            and(
              eq(projectExclusionCodes.id, codeId),
              eq(projectExclusionCodes.projectId, data.projectId),
            ),
          ),
      ),
    );

    return { success: true };
  });
